// **The practice path's production seams** (`rounds-practice-sandbox` task 5.12; `client-app` "The
// root owns the practice seams and no screen constructs them").
//
// `PracticeEnvironment`'s three device seams — `createWorker`, `compile`, `traceStore` — had no
// production default and could not have one until `waggle-wasm` bound the engine. They have one now,
// and it lives here so `bootstrap.tsx` reads the way it already reads for the renderer:
// `deps.x ?? createProductionX()`. That shape is the point. Before this, `main.tsx` supplied no
// practice environment at all, so `RoundsController` built its run seam as `null`, `run` resolved to
// `null`, and Home's unconditionally-mounted "Play in sandbox" CTA dead-ended at Run — a seam only the
// tests supplied is a seam production never got.
//
// ## One module load on the main thread, and one per run in a worker
//
// The main thread needs the module for two things: the compile gate, and BLAKE3 to verify a published
// trace's components. `practice-trace-store.ts` says the root "passes the module it has already loaded
// to run the sim — the store never loads one of its own", and that stays true *of the store*; what has
// changed since it was written is that the sim no longer runs on this thread, so the module the store
// verifies with cannot be the sim's. There are therefore two instances of one cached `.wasm`: the main
// thread's (compile + verify) and each run worker's (engine + writer). That is not a duplication that
// could have been avoided — the writer must be in a worker (OPFS sync handles) and the compile gate must
// be on the thread that presses Run (it precedes the request, so a promise across a worker boundary
// would put a thread hop inside the gate) — but it is a cost worth stating rather than discovering.
//
// {@link lazyPracticeWasm} is what keeps it to one load per thread and, just as importantly, keeps a
// *failed* load from being retried on every keystroke: a device with no module gets one 404, and every
// subsequent gate answers from the remembered reason.

import {
  createPracticeDocumentCompiler,
  unavailableEngineCompiler,
  type PracticeWasmModule,
} from './practice-engine';
import { loadPracticeWasmModule } from './practice-wasm';
import { createPracticeWorkerFactory } from './practice-worker-host';
import {
  openLocalTraceReader,
  wasmLocalTraceVerifier,
  type LocalTraceReadStore,
  type OpfsDirectoryHandle,
} from './practice-trace-store';
import { browserLockManager } from './run-lease';
import type {
  PracticeCompileOutcome,
  PracticeRunRecord,
  PracticeTraceStore,
} from './practice-runner';
import type { PracticeEnvironment } from './RoundsController';
import type { TraceLoad } from '../../net/rounds-review-source';

/** A module load, attempted at most once. Resolves to the module or to the reason it could not. */
export type PracticeWasmHandle = () => Promise<
  { readonly kind: 'ready'; readonly module: PracticeWasmModule } | { readonly kind: 'unavailable'; readonly reason: string }
>;

/** Memoizes one load — success or failure — per call of this function. */
export function lazyPracticeWasm(
  load: () => Promise<PracticeWasmModule> = () => loadPracticeWasmModule(),
): PracticeWasmHandle {
  let pending: Promise<
    { kind: 'ready'; module: PracticeWasmModule } | { kind: 'unavailable'; reason: string }
  > | null = null;
  return () => {
    pending ??= load().then(
      (module) => ({ kind: 'ready' as const, module }),
      (error: unknown) => ({
        kind: 'unavailable' as const,
        reason: error instanceof Error ? error.message : String(error),
      }),
    );
    return pending;
  };
}

/**
 * The on-device compile gate, over a lazily loaded module.
 *
 * Asynchronous, which {@link PracticeDocumentCompiler} explicitly permits ("synchronous or not, but
 * never optional"), and the run controller already awaits it before the run-start request — so the
 * gate's position relative to the wire is unchanged.
 */
export function deviceDocumentCompiler(handle: PracticeWasmHandle) {
  return async (document: string): Promise<PracticeCompileOutcome> => {
    const loaded = await handle();
    if (loaded.kind === 'unavailable') {
      return unavailableEngineCompiler(loaded.reason)(document);
    }
    return createPracticeDocumentCompiler(loaded.module)(document);
  };
}

/** The origin-private root, or a thrown reason. Separated so a test never needs a real `navigator`. */
async function opfsRoot(): Promise<OpfsDirectoryHandle> {
  const storage = globalThis.navigator?.storage as
    | { getDirectory?: () => Promise<unknown> }
    | undefined;
  if (typeof storage?.getDirectory !== 'function') {
    throw new Error('this origin offers no private file storage');
  }
  return (await storage.getDirectory()) as OpfsDirectoryHandle;
}

/**
 * The main thread's **read-only** view of the device's traces.
 *
 * `openLocalTraceReader`, never `openLocalTraceStore` — see `LocalTraceReadStore`'s doc comment for the
 * whole ownership decision. In one line: the reclaiming open would delete the generation a run's worker
 * is writing into at that moment.
 *
 * Opened lazily and once, so nothing touches OPFS until the player has a practice session, and a
 * device that cannot open it answers honestly rather than throwing into the screen: an unreadable store
 * lists no runs and reports a corruption with the reason, which is what the shipped `TraceLoad`
 * vocabulary can say.
 */
export function deviceTraceStore(
  handle: PracticeWasmHandle,
  open: (module: PracticeWasmModule) => Promise<LocalTraceReadStore> = async (module) =>
    openLocalTraceReader({
      root: await opfsRoot(),
      verifier: wasmLocalTraceVerifier(module),
      // The main thread reclaims nothing at open, but it still needs the lease facility: its two
      // lease-gated capabilities (`listAbandonedRuns`, `reclaim`) are how the interrupted-run report
      // is both made and retired, and both must be able to tell "no writer holds this" from "we
      // cannot tell".
      locks: browserLockManager(),
    }),
): PracticeTraceStore {
  let pending: Promise<LocalTraceReadStore | { unavailable: string }> | null = null;
  const reader = (): Promise<LocalTraceReadStore | { unavailable: string }> => {
    pending ??= (async () => {
      const loaded = await handle();
      if (loaded.kind === 'unavailable') return { unavailable: loaded.reason };
      return open(loaded.module);
    })().catch((error: unknown) => ({
      unavailable: error instanceof Error ? error.message : String(error),
    }));
    return pending;
  };

  return {
    async open(runId: number): Promise<TraceLoad> {
      const store = await reader();
      if ('unavailable' in store) {
        return {
          kind: 'corrupt',
          component: 'detail',
          message: `This device could not read its own replays: ${store.unavailable}`,
        };
      }
      return store.asPracticeTraceStore().open(runId);
    },
    async listRuns(): Promise<readonly PracticeRunRecord[]> {
      const store = await reader();
      // No store means no runs — which is true, and is the answer the run rail can render. It is not
      // an error state: a device that has never practised is in exactly this position.
      return 'unavailable' in store ? [] : store.listRuns();
    },
    async listAbandonedRuns(): Promise<readonly number[]> {
      const store = await reader();
      // An unreadable store reports **nothing interrupted**, which is the same "possibly live" default
      // an unknown lease produces: we could not establish that a run was abandoned, so we do not say
      // one was. Telling a player their run was interrupted because we could not open the store would
      // be a guess dressed as a fact.
      return 'unavailable' in store ? [] : store.listAbandonedRuns();
    },
    async reclaim(runId: number): Promise<boolean> {
      const store = await reader();
      return 'unavailable' in store ? false : store.reclaim(runId);
    },
  };
}

/**
 * **The production practice environment**, with every caller-supplied seam winning.
 *
 * Merged field-by-field rather than by a spread of defaults over the override, so a test that supplies
 * only `capabilities` (which is all a jsdom process must replace to reach the gate) still gets the real
 * worker factory, the real compiler and the real store — which is what makes
 * `bootstrap.practice.test.tsx`'s production-wiring assertion mean something.
 */
export function productionPracticeEnvironment(
  supplied: PracticeEnvironment = {},
): PracticeEnvironment {
  const wasm = lazyPracticeWasm();
  return {
    ...supplied,
    createWorker: supplied.createWorker ?? createPracticeWorkerFactory(),
    compile: supplied.compile ?? deviceDocumentCompiler(wasm),
    traceStore: supplied.traceStore ?? deviceTraceStore(wasm),
  };
}
